import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { FocusRing } from '@react-aria/focus';
import CloseX from '../../icons/icon-close-modal.svg';
import isEnterOrSpace from '../../helpers/isEnterOrSpace';
import isJSX from '../../helpers/isJSX';
import CustomReactMarkdown from '../MarkdownRenderers/CustomReactMarkdown';
import { styler } from '../../helpers/classNameHelper';


const InfoModal = (props) => {
  const {
    children,
    headerIcon,
    hideIcon,
    isOpen,
    onRequestClose,
    styleFlag,
    buttonElement,
    contentLabel,
    openTrack,
    contentLinkTrack,
  } = props;

  const style = styler(styleFlag);
  const [modalIsOpen, setModalIsOpen] = useState();

  const appElement = document.body.querySelector('.se-page');
  const ButtonElement = buttonElement;
  const modalProps = {
    ariaHideApp: !!appElement,
    appElement,
  };

  useEffect(() => {
    if (isOpen !== null && modalIsOpen !== isOpen) {
      setModalIsOpen(isOpen);
    }
  }, [isOpen]);

  const openModal = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const { disabled } = props;

    if (disabled) {
      return;
    }

    if (openTrack) {
      openTrack();
    }

    setModalIsOpen(true);
  };

  const closeModal = (event) => {
    event.stopPropagation();
    event.preventDefault();

    setModalIsOpen(false);
  };

  const renderHeader = () => {
    const { header, subheader } = props;

    if (!header) {
      return null;
    }

    return isJSX(header) ?
      header :
      (
        <h1>
          {subheader &&
          <span className="info-modal__subheading">{subheader}</span>}
          {header}
        </h1>
      );
  };

  const renderMessage = () => {
    const { message } = props;

    if (!message) {
      return null;
    }

    if (isJSX(message)) {
      return message;
    }

    const linkActions = {};

    if (contentLinkTrack instanceof Function) {
      linkActions['*'] = contentLinkTrack;
    }

    return (
      <CustomReactMarkdown
        linkActions={linkActions}
        linkMeta={['allowIcon', 'openNewTab']}
        source={message}
      />
    );
  };

  const closeHandler = onRequestClose || closeModal;

  return (
    <>
      {children && (
        <FocusRing focusRingClass={style('info-modal__trigger-focused')}>
          <ButtonElement
            role="button"
            tabIndex="0"
            className={style('info-modal__trigger')}
            onClick={openModal}
            onKeyPress={event => isEnterOrSpace(event) && openModal(event)}
          >
            {children}
          </ButtonElement>
        </FocusRing>
      )}
      <Modal
        className={style('info-modal')}
        isOpen={modalIsOpen}
        onRequestClose={closeHandler}
        contentLabel={contentLabel}
        overlayClassName="info-modal__overlay"
        {...modalProps}
      >
        <header className={style('info-modal__header')}>
          <FocusRing focusRingClass={style('info-modal__close-focused')}>
            <div
              role="button"
              aria-label="Close"
              tabIndex="0"
              className="info-modal__close"
              onClick={closeHandler}
              onKeyPress={event => isEnterOrSpace(event) && closeHandler(event)}
            >
              <CloseX className="info-modal__close--icon" />
            </div>
          </FocusRing>
          {!hideIcon && headerIcon && (
            <span className={style('info-modal__info-icon')}>
              {headerIcon}
            </span>
          )}
          {renderHeader()}
        </header>
        <main className={style('info-modal__main')}>
          {renderMessage()}
        </main>
      </Modal>
    </>
  );
};

InfoModal.propTypes = {
  children: (props, propName) => {
    const { [propName]: prop, isOpen, onRequestClose } = props;

    if (prop && (isOpen !== null || onRequestClose !== null)) {
      return new Error(`[${propName}] cannot be used in conjunction with external state handlers [isOpen] and [onRequestClose]`);
    }

    return null;
  },
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  subheader: PropTypes.string,
  headerIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  styleFlag: PropTypes.string.isRequired,
  hideIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  buttonElement: PropTypes.string,
  contentLabel: PropTypes.string.isRequired,
  openTrack: PropTypes.func,
  contentLinkTrack: PropTypes.func,
};

InfoModal.defaultProps = {
  children: null,
  header: null,
  subheader: null,
  headerIcon: null,
  message: '',
  hideIcon: false,
  disabled: false,
  isOpen: null,
  onRequestClose: null,
  buttonElement: 'div',
  openTrack: null,
  contentLinkTrack: null,
};

export default InfoModal;
